import { z } from 'zod';
import {
  LocationSchema,
  DateRangeSchema,
  PriceSchema,
  Interest,
  InterestSchema,
  ServiceTierLevelSchema,
  DietaryRestrictionSchema,
  TripMotivationSchema,
} from './base';
import { DailyItinerarySchema } from './itinerary';
import { ActivitySchema, Restaurant, RestaurantSchema } from './google';

// Simplified Trip Preferences Schema - only essential fields
const TripPreferencesSchema = z.object({
  // Core trip information
  serviceTier: ServiceTierLevelSchema,
  tripMotivation: TripMotivationSchema.optional().nullable(),
  destination: LocationSchema,
  dates: DateRangeSchema,

  // Simplified travelers info
  travelers: z.object({
    adults: z.number().min(1).default(2),
    children: z.number().min(0).default(0),
    infants: z.number().min(0).default(0),
    groupType: z
      .enum(['solo', 'couple', 'family', 'friends', 'business'])
      .default('couple'),
  }),

  // Activity preferences (kept for personalization)
  activityPreferences: z
    .object({
      interests: z.array(InterestSchema).default([]),
      priceLevel: z
        .enum([
          'free',
          'inexpensive',
          'moderate',
          'expensive',
          'very_expensive',
        ])
        .default('moderate'),
      customActivities: z.array(z.string()).default([]).optional(),
    })
    .optional()
    .default({
      interests: [],
      priceLevel: 'moderate',
      customActivities: [],
    }),

  // Basic dining preferences
  diningPreferences: z
    .object({
      dietaryRestrictions: z.array(DietaryRestrictionSchema).default([]),
      priceLevel: z
        .enum([
          'free',
          'inexpensive',
          'moderate',
          'expensive',
          'very_expensive',
        ])
        .default('moderate')
        .optional(),
    })
    .optional()
    .nullable(),
});

export const TripStatusSchema = z.enum([
  'planning',
  'booked',
  'published',
  'completed',
  'cancelled',
]);
export type TripStatus = z.infer<typeof TripStatusSchema>;

// Complete Trip Schema
const TripSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional().default(''),
  preferences: TripPreferencesSchema,
  searchResults: z
    .object({
      restaurants: z.array(RestaurantSchema).optional().default([]),
      activities: z.array(ActivitySchema).optional().default([]),
    })
    .optional()
    .default({ restaurants: [], activities: [] }),
  itinerary: z.array(DailyItinerarySchema),
  totalCost: PriceSchema.optional().nullable(),
  status: TripStatusSchema.default('planning'),
  collaborators: z
    .array(
      z.object({
        userId: z.string(),
        email: z.string(),
        role: z.enum(['owner', 'editor', 'viewer']),
      })
    )
    .optional()
    .default([]),
  createdAt: z.date(),
  updatedAt: z.date(),
  publishedAt: z.date().optional().nullable(),
});

// Scrollytelling-specific schemas
export const ScrollytellingSlideSchema = z.object({
  id: z.string(),
  type: z.enum([
    'hero',
    'itinerary-item',
    'day-overview',
    'travel-tip',
    'gallery',
    'cta',
    'overnight-stay',
  ]),
  title: z.string(),
  subtitle: z.string().optional().default(''),
  content: z.string().optional().default(''),
  backgroundImage: z.string().optional().nullable(),
  backgroundColor: z.string().optional().default('#ffffff'),
  textColor: z.string().optional().default('#000000'),

  // Related itinerary item (if applicable)
  relatedItemId: z.string().optional().nullable(),
  relatedDayIndex: z.number().optional().nullable(),

  // Animation settings
  animations: z
    .object({
      entry: z.string().optional().default('fadeInUp'),
      exit: z.string().optional().default('fadeOutDown'),
      duration: z.number().optional().default(1000),
      delay: z.number().optional().default(0),
    })
    .optional()
    .default({
      entry: 'fadeInUp',
      exit: 'fadeOutDown',
      duration: 1000,
      delay: 0,
    }),

  // CTAs and interactive elements
  ctas: z
    .array(
      z.object({
        id: z.string(),
        text: z.string(),
        url: z.string(),
        type: z.enum([
          'booking',
          'website',
          'map',
          'phone',
          'email',
          'scroll-next',
        ]),
        openInNewTab: z.boolean().default(true),
        style: z.enum(['primary', 'secondary', 'outline']).default('primary'),
      })
    )
    .optional()
    .default([]),

  // Info icons and hints
  infoPoints: z
    .array(
      z.object({
        id: z.string(),
        x: z.number(), // Position as percentage (0-100)
        y: z.number(), // Position as percentage (0-100)
        title: z.string(),
        content: z.string(),
        icon: z.string().optional().default('info'),
        color: z.string().optional().default('#3B82F6'),
      })
    )
    .optional()
    .default([]),

  // Layout and positioning
  layout: z
    .object({
      textPosition: z
        .enum(['left', 'center', 'right', 'overlay'])
        .default('center'),
      imagePosition: z
        .enum(['background', 'left', 'right', 'top', 'bottom'])
        .default('background'),
      textAlignment: z.enum(['left', 'center', 'right']).default('center'),
    })
    .optional()
    .default({
      textPosition: 'center',
      imagePosition: 'background',
      textAlignment: 'center',
    }),
});

export const ScrollytellingStorySchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional().default(''),
  description: z.string().optional().default(''),
  coverImage: z.string().optional().nullable(),

  // Story configuration
  config: z
    .object({
      autoProgress: z.boolean().default(false),
      progressDuration: z.number().optional().default(5000), // ms per slide
      showProgress: z.boolean().default(true),
      showNavigation: z.boolean().default(true),
      enableKeyboardNav: z.boolean().default(true),
      enableSwipeNav: z.boolean().default(true),
    })
    .optional()
    .default({
      autoProgress: false,
      progressDuration: 5000,
      showProgress: true,
      showNavigation: true,
      enableKeyboardNav: true,
      enableSwipeNav: true,
    }),

  // Slides
  slides: z.array(ScrollytellingSlideSchema),

  // SEO and sharing
  seo: z
    .object({
      metaTitle: z.string().optional().default(''),
      metaDescription: z.string().optional().default(''),
      ogImage: z.string().optional().nullable(),
      canonicalUrl: z.string().optional().default(''),
    })
    .optional()
    .nullable(),

  // Analytics
  viewCount: z.number().default(0),
  shareCount: z.number().default(0),
  completionRate: z.number().min(0).max(100).default(0),

  // Timestamps
  createdAt: z.date(),
  updatedAt: z.date(),
  publishedAt: z.date().optional().nullable(),
});

// Published Trip Schema (for scrollytelling)
const PublishedTripSchema = TripSchema.extend({
  status: z.literal('published'),
  publishedAt: z.date(),
  // publishSettings: z.object({
  //   isPublic: z.boolean(),
  //   allowComments: z.boolean(),
  //   customCss: z.string().optional().default(''),
  //   animations: z
  //     .object({
  //       enabled: z.boolean().default(true),
  //       transitions: z
  //         .array(
  //           z.object({
  //             trigger: z.string(),
  //             animation: z.string(),
  //             duration: z.number().optional().default(300),
  //             easing: z.string().optional().default('ease'),
  //           })
  //         )
  //         .optional()
  //         .default([]),
  //     })
  //     .optional()
  //     .default({ enabled: true, transitions: [] }),
  // }),
  slug: z.string(),

  // Scrollytelling story data
  scrollytellingStory: ScrollytellingStorySchema.optional().nullable(),

  viewCount: z.number().default(0),
  likes: z.number().default(0),
  comments: z
    .array(
      z.object({
        id: z.string(),
        userId: z.string(),
        username: z.string(),
        content: z.string(),
        createdAt: z.date(),
        replies: z
          .array(
            z.lazy(() =>
              z.object({
                id: z.string(),
                userId: z.string(),
                username: z.string(),
                content: z.string(),
                createdAt: z.date(),
              })
            )
          )
          .optional()
          .default([]),
      })
    )
    .optional()
    .default([]),
});

export { TripPreferencesSchema, TripSchema, PublishedTripSchema };

export type TripPreferences = z.infer<typeof TripPreferencesSchema>;
export type Trip = z.infer<typeof TripSchema>;
export type PublishedTrip = z.infer<typeof PublishedTripSchema>;
export type ScrollytellingSlide = z.infer<typeof ScrollytellingSlideSchema>;
export type ScrollytellingStory = z.infer<typeof ScrollytellingStorySchema>;

// Search result types for easier handling
export type SearchResults = {
  restaurants?: Restaurant[];
  activities?: Interest[];
};

// Trip planning workflow types
export type TripPlanningStep =
  | 'preferences'
  | 'search'
  | 'selection'
  | 'itinerary'
  | 'booking'
  | 'publish';

export type TripPlanningState = {
  currentStep: TripPlanningStep;
  preferences?: TripPreferences;
  searchResults?: SearchResults;
  selectedItems: {
    restaurants?: Restaurant[];
    activities?: Interest[];
  };
  trip?: Trip;
};
